import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import {
  findOrThrow,
  getOrThrow,
  isNotFoundError,
  loadErrorMessage,
} from "shared/loadOrError";
import type { Partner } from "../Partners/PartnersStore";
import type {
  PartnerEmailDomain,
  RelationshipItem,
} from "../meetingIntelligence.types";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";
import { THROW_ON_ERROR } from "shared/dialogSubmit";
import { findAndEvict } from "shared/findAndEvict";
import { watchPartnerLedger } from "./partnerLedgerLive";

/** The ledger read's page: the newest rows, and the most a read evicts against (findAndEvict). */
const LEDGER_LIMIT = 200;

/**
 * A Feathers `find` result as the ServiceStores surface it: the paginated
 * envelope `{ data }` or a bare array (custom/derived services).
 */
type FindResult<T> = { total?: number; data?: T[] } | T[] | null | undefined;

function rowsOf<T>(result: FindResult<T>): T[] {
  if (Array.isArray(result)) return result;
  return result?.data ?? [];
}

/**
 * Route ViewStore for the Partner 360 detail hub. Plain MobX class (route
 * ViewStores never extend ViewStore). Reads the registered read-ai ServiceStores
 * by hand: `partners` (the record), `partner-email-domains` (the corporate
 * domains that auto-route meetings to this partner), and `relationship-items`
 * (the expectation/need/commitment ledger where the partner is the subject).
 *
 * Registered as `partnerDetailStore`; the `partners` ServiceStore is resolved
 * via serviceStoreManager.getStore (see [[web-servicestore-access-collision]]).
 *
 * LIVE (#3820): while the page is open (`subscribe`, from the route), a
 * colleague's change to this partner's ledger, and a meeting analysis that
 * wrote relationship signals, re-read the ledger (partnerLedgerLive.ts).
 */
class PartnerDetailStore {
  rootStore: RootStore;

  partnerId: string | null = null;
  partner: Partner | null = null;
  loading = false;
  error: string | null = null;

  // ---- Email domains (Overview tab) ----
  emailDomains: PartnerEmailDomain[] = [];
  domainsLoading = false;
  /** The domains did not load — not "No domains mapped yet". */
  domainsError: string | null = null;
  newDomain = "";
  domainSaving = false;

  // ---- Relationship ledger ----
  relationshipItems: RelationshipItem[] = [];
  relationshipLoading = false;
  /** The ledger did not load — not "No relationship items captured yet". */
  relationshipError: string | null = null;

  /**
   * Bumped by setPartnerId, dispose and reset: a read or a mutation's follow-up
   * load that was started for an earlier partner (or before the page was left)
   * answers about a partner no longer on screen and must write nothing.
   */
  private partnerSeq = 0;
  /**
   * Bumped by every ledger read: a live re-read, a Refresh or a write's
   * follow-up can overlap, and an older answer must never land over a newer.
   */
  private ledgerSeq = 0;
  /** The live subscription (plumbing, not state). */
  private stopLive: (() => void) | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable<this, "partnerSeq" | "ledgerSeq" | "stopLive">(this, {
      rootStore: false,
      partnerSeq: false,
      ledgerSeq: false,
      stopLive: false,
    });
  }

  /** Follow the open partner's ledger (route onEnter). Idempotent; ended by `dispose` and `reset`. */
  subscribe() {
    if (this.stopLive) return;
    this.stopLive = watchPartnerLedger(
      this.rootStore,
      () => ({ partnerId: this.partnerId, listedIds: new Set(this.relationshipItems.map((r) => String(r.id))) }),
      () => void this.loadRelationshipItems()
    );
  }

  private stopLiveUpdates() {
    this.stopLive?.();
    this.stopLive = null;
  }

  /**
   * Taken where a call starts: true while it is still the partner on screen in
   * the session that asked. Check it after every await before a state write, a
   * flag reset or a follow-up load.
   */
  private live(): () => boolean {
    const seq = this.partnerSeq;
    const ended = sessionMark(this.rootStore);
    return () => seq === this.partnerSeq && !ended();
  }

  // ---- Registered ServiceStore handles ------------------------------------

  private get partnersService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("partners") ?? this.rootStore.partnersStore) as unknown as ServiceStoreType | null) ?? null;
  }
  private get partnerDomainsService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("partner-email-domains") ?? this.rootStore.partnerEmailDomainsStore) as unknown as ServiceStoreType | null) ?? null;
  }
  private get relationshipItemsService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("relationship-items") ?? this.rootStore.relationshipItemsStore) as unknown as ServiceStoreType | null) ?? null;
  }

  setPartnerId(id: string) {
    this.partnerSeq++;
    // The calls in flight for the earlier partner skip their flag resets from
    // here on, so the flags they would have cleared are cleared now.
    this.loading = false;
    this.domainsLoading = false;
    this.domainSaving = false;
    this.relationshipLoading = false;
    this.partnerId = id;
  }

  // ---- Loading ------------------------------------------------------------

  async loadPartner() {
    if (!this.partnerId) return;
    const live = this.live();
    this.loading = true;
    this.error = null;
    try {
      // getOrThrow: the ServiceStore resolves undefined on a failed get, and
      // the page said "Partner not found" over a load failure.
      const partner = await getOrThrow<Partner>(this.partnersService, this.partnerId);
      // Another partner (or none, or another person) is on screen now: this
      // answer is not theirs, and fanning out would load the wrong ledger.
      if (!live()) return;
      runInAction(() => {
        this.partner = partner ?? null;
      });
      this.loadDomains();
      this.loadRelationshipItems();
    } catch (err: unknown) {
      // Signed out under this call, or the partner changed: what is in the
      // store now is not this call's to touch.
      if (isSessionEnded(err) || !live()) return;
      runInAction(() => {
        // A 404 is an answer ("Partner not found"), not a load failure.
        // Either way nothing from an EARLIER load stays on screen: a stale
        // partner (and its domains and ledger) beside the error reads as
        // current data, and after a route change it is another partner's.
        this.partner = null;
        this.emailDomains = [];
        this.relationshipItems = [];
        if (isNotFoundError(err)) return;
        this.error = loadErrorMessage(err, "Failed to load partner");
      });
      console.error("Failed to load partner:", err);
    } finally {
      // Signed out or moved on meanwhile: reset() / dispose() cleared the flag,
      // and a load the next partner started may be the one in flight now.
      if (live()) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  async loadDomains() {
    if (!this.partnerId) return;
    const live = this.live();
    this.domainsLoading = true;
    this.domainsError = null;
    try {
      // findOrThrow: a swallowed failure read as "No domains mapped yet".
      const result = await findOrThrow(this.partnerDomainsService, {
        query: { partnerId: this.partnerId, $limit: 200, $sort: { domain: 1 } },
      });
      if (!live()) return;
      runInAction(() => {
        this.emailDomains = rowsOf<PartnerEmailDomain>(
          result as unknown as FindResult<PartnerEmailDomain>
        );
      });
    } catch (err) {
      // Signed out under this call, or the partner changed.
      if (isSessionEnded(err) || !live()) return;
      runInAction(() => {
        // The list a failed reload leaves behind is not this partner's list
        // any more — show the error alone, not the error beside stale rows.
        this.emailDomains = [];
        this.domainsError = loadErrorMessage(err, "Failed to load email domains");
      });
      console.error("Failed to load partner domains:", err);
    } finally {
      if (live()) {
        runInAction(() => {
          this.domainsLoading = false;
        });
      }
    }
  }

  async loadRelationshipItems() {
    if (!this.partnerId) return;
    const partnerId = this.partnerId;
    const live = this.live();
    // The newest read wins: a live re-read can start while an earlier read is
    // still out, and that earlier answer may predate the change.
    const seq = ++this.ledgerSeq;
    const current = () => live() && seq === this.ledgerSeq;
    this.relationshipLoading = true;
    this.relationshipError = null;
    try {
      const service = this.relationshipItemsService;
      // findOrThrow: a swallowed failure read as "No relationship items".
      // findAndEvict: the shared `relationship-items` cache also drops this
      // partner's rows that the complete answer no longer holds — removed
      // while this tab missed the event (#2654); the page's copy is replaced.
      const result = await findAndEvict(
        service,
        (row) => row.subjectType === "partner" && String(row.subjectId ?? "") === partnerId,
        () =>
          findOrThrow(service, {
            query: {
              subjectType: "partner",
              subjectId: partnerId,
              $limit: LEDGER_LIMIT,
              $sort: { createdAt: -1 },
            },
          }),
        { limit: LEDGER_LIMIT },
      );
      if (!current()) return;
      runInAction(() => {
        this.relationshipItems = rowsOf<RelationshipItem>(
          result as unknown as FindResult<RelationshipItem>
        );
      });
    } catch (err) {
      // Signed out under this call, the partner changed, or a newer read is out.
      if (isSessionEnded(err) || !current()) return;
      runInAction(() => {
        this.relationshipItems = [];
        this.relationshipError = loadErrorMessage(err, "Failed to load the relationship ledger");
      });
      console.error("Failed to load partner relationship ledger:", err);
    } finally {
      if (current()) {
        runInAction(() => {
          this.relationshipLoading = false;
        });
      }
    }
  }

  // ---- Derived ------------------------------------------------------------

  get liveRelationshipItems(): RelationshipItem[] {
    return this.relationshipItems.filter((r) => !r.deletedAt);
  }

  /** Open ledger items grouped by kind (expectation/need/commitment). */
  get relationshipItemsByKind(): Record<string, RelationshipItem[]> {
    const groups: Record<string, RelationshipItem[]> = {};
    for (const item of this.liveRelationshipItems) {
      const key = item.kind || "other";
      (groups[key] ||= []).push(item);
    }
    return groups;
  }

  get openRelationshipCount(): number {
    return this.liveRelationshipItems.filter((r) => r.status === "open").length;
  }

  // ---- Email domain CRUD (Overview tab; API enforces admin) ----------------

  setNewDomain(value: string) {
    this.newDomain = value;
  }

  get isNewDomainValid(): boolean {
    // A bare corporate domain — at least one dot, no "@" or scheme.
    const d = this.newDomain.trim().toLowerCase();
    return /^[a-z0-9.-]+\.[a-z]{2,}$/.test(d) && !d.includes("@");
  }

  async addDomain(): Promise<boolean> {
    if (!this.partnerId || !this.isNewDomainValid) return false;
    const live = this.live();
    this.domainSaving = true;
    try {
      // THROW_ON_ERROR here and below: the ServiceStore otherwise RESOLVES a
      // refusal (and a sign-out) as null, which read as done — and the reload
      // after it went out in whichever session came next.
      await this.partnerDomainsService?.create(
        {
          partnerId: this.partnerId,
          domain: this.newDomain.trim().toLowerCase(),
        } as never,
        THROW_ON_ERROR,
      );
      // The domain is saved; the page it was typed on is gone (or another
      // person's now): clear no input and reload no list there.
      if (!live()) return true;
      runInAction(() => {
        this.newDomain = "";
      });
      await this.loadDomains();
      return true;
    } catch (err) {
      console.error("Failed to add partner domain:", err);
      return false;
    } finally {
      if (live()) {
        runInAction(() => {
          this.domainSaving = false;
        });
      }
    }
  }

  async removeDomain(id: number): Promise<boolean> {
    const live = this.live();
    try {
      await this.partnerDomainsService?.remove(id, THROW_ON_ERROR);
      if (!live()) return true;
      await this.loadDomains();
      return true;
    } catch (err) {
      console.error("Failed to remove partner domain:", err);
      return false;
    }
  }

  // ---- Relationship ledger mutations ---------------------------------------

  async setRelationshipItemStatus(
    id: number,
    status: "open" | "resolved"
  ): Promise<boolean> {
    const live = this.live();
    try {
      await this.relationshipItemsService?.patch(id, { status }, THROW_ON_ERROR);
      if (!live()) return true;
      await this.loadRelationshipItems();
      return true;
    } catch (err) {
      console.error("Failed to update relationship item:", err);
      return false;
    }
  }

  async removeRelationshipItem(id: number): Promise<boolean> {
    const live = this.live();
    try {
      await this.relationshipItemsService?.remove(id, THROW_ON_ERROR);
      if (!live()) return true;
      await this.loadRelationshipItems();
      return true;
    } catch (err) {
      console.error("Failed to remove relationship item:", err);
      return false;
    }
  }

  // ---- Lifecycle ----------------------------------------------------------

  dispose() {
    // Left the page: no live re-read may land after this.
    this.stopLiveUpdates();
    runInAction(() => {
      // In-flight calls now skip their flag resets (they are not the live
      // partner), so the flags they would have cleared are cleared here.
      this.partnerSeq++;
      this.loading = false;
      this.domainsLoading = false;
      this.domainSaving = false;
      this.relationshipLoading = false;
      this.partnerId = null;
      this.partner = null;
      this.emailDomains = [];
      this.relationshipItems = [];
      this.newDomain = "";
      this.error = null;
      this.domainsError = null;
      this.relationshipError = null;
    });
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.stopLiveUpdates();
    this.partnerSeq++;
    this.partnerId = null;
    this.partner = null;
    this.loading = false;
    this.error = null;
    this.emailDomains = [];
    this.domainsLoading = false;
    this.domainsError = null;
    this.newDomain = "";
    this.domainSaving = false;
    this.relationshipItems = [];
    this.relationshipLoading = false;
    this.relationshipError = null;
  }
}

export default PartnerDetailStore;
